import React, { useEffect, useState } from "react";
import useSWR from "swr";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, RotateCcw, Eye, MessageSquareText, Loader2 } from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function WaTemplates() {
  const { data, mutate: reload } = useSWR("/wa/templates", fetcher);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null); // {kind, rendered}

  useEffect(() => {
    if (data?.templates) {
      setDrafts(Object.fromEntries(Object.entries(data.templates).map(([k, v]) => [k, v.value])));
    }
  }, [data?.templates]);

  const kinds = data?.templates ? Object.keys(data.templates) : [];
  const placeholders = data?.placeholders || [];

  const dirty = data?.templates
    ? kinds.some((k) => (drafts[k] ?? "") !== (data.templates[k].value ?? ""))
    : false;

  const saveAll = async () => {
    setSaving(true);
    try {
      const payload = { templates: {} };
      kinds.forEach((k) => {
        // send empty string as "reset to default" signal
        const v = (drafts[k] ?? "").trim();
        const original = data.templates[k].value;
        if (v !== original) payload.templates[k] = v;
      });
      const { data: refreshed } = await api.put("/wa/templates", payload);
      toast.success("Template tersimpan.");
      reload(refreshed);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal menyimpan template");
    } finally {
      setSaving(false);
    }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset SEMUA template ke default bawaan? Perubahan akan hilang.")) return;
    setSaving(true);
    try {
      const { data: refreshed } = await api.post("/wa/templates/reset");
      toast.success("Semua template dikembalikan ke default.");
      reload(refreshed);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal reset");
    } finally {
      setSaving(false);
    }
  };

  const resetOne = async (k) => {
    if (!data?.templates?.[k]) return;
    if (data.templates[k].is_default) {
      // already default — just visually sync draft
      setDrafts((d) => ({ ...d, [k]: data.templates[k].default }));
      return;
    }
    setSaving(true);
    try {
      const { data: refreshed } = await api.put("/wa/templates", { templates: { [k]: "" } });
      toast.success(`Template "${data.templates[k].label}" dikembalikan ke default.`);
      reload(refreshed);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal reset template");
    } finally {
      setSaving(false);
    }
  };

  const doPreview = async (k) => {
    try {
      const { data: res } = await api.post("/wa/templates/preview", { template: drafts[k] ?? "" });
      setPreview({ kind: k, rendered: res.rendered });
    } catch (e) {
      toast.error("Gagal menghasilkan preview");
    }
  };

  const insertPlaceholder = (k, ph) => {
    setDrafts((d) => ({ ...d, [k]: (d[k] ?? "") + " " + ph }));
  };

  if (!data) return <div className="text-sm text-muted-foreground">Memuat template...</div>;

  return (
    <div className="space-y-6" data-testid="wa-templates-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">Integrasi Fonnte</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Template Pesan WhatsApp</h1>
          <p className="text-sm text-muted-foreground mt-1">Sesuaikan gaya bahasa pesan H-7, jatuh tempo, dan kwitansi.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={resetAll} disabled={saving} className="gap-2" data-testid="wa-reset-all">
            <RotateCcw className="h-4 w-4" /> Reset Semua
          </Button>
          <Button onClick={saveAll} disabled={saving || !dirty} className="gap-2" data-testid="wa-save-all">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Simpan Perubahan
          </Button>
        </div>
      </header>

      <Card>
        <CardContent className="p-4">
          <div className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-2">Placeholder tersedia</div>
          <div className="flex flex-wrap gap-2">
            {placeholders.map((p) => (
              <div key={p.key} className="rounded-md border border-border/60 px-2 py-1 text-xs bg-muted/40">
                <span className="font-mono font-semibold">{p.key}</span>
                <span className="text-muted-foreground"> — {p.help}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4">
        {kinds.map((k) => {
          const meta = data.templates[k];
          const value = drafts[k] ?? "";
          const changed = value !== meta.value;
          return (
            <Card key={k} data-testid={`wa-template-card-${k}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <MessageSquareText className="h-4 w-4 text-primary" />
                      <div className="font-display text-lg font-medium tracking-tight">{meta.label}</div>
                      {meta.is_default && !changed && (
                        <Badge variant="secondary" className="rounded-full text-[10px] uppercase tracking-wider">Default</Badge>
                      )}
                      {changed && (
                        <Badge className="rounded-full text-[10px] uppercase tracking-wider bg-warning/15 text-warning">Belum disimpan</Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{meta.description}</div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => doPreview(k)} className="gap-1.5" data-testid={`wa-preview-${k}`}>
                      <Eye className="h-4 w-4" /> Preview
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => resetOne(k)} className="gap-1.5" data-testid={`wa-reset-${k}`}>
                      <RotateCcw className="h-4 w-4" /> Ke Default
                    </Button>
                  </div>
                </div>
                <Textarea
                  rows={10}
                  value={value}
                  onChange={(e) => setDrafts({ ...drafts, [k]: e.target.value })}
                  className="font-mono text-sm leading-relaxed"
                  placeholder="Tulis pesan Anda di sini... gunakan placeholder seperti {name}, {amount}, {due_date}"
                  data-testid={`wa-textarea-${k}`}
                />
                <div className="flex flex-wrap gap-1.5">
                  {placeholders.map((p) => (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => insertPlaceholder(k, p.key)}
                      className="text-[10px] font-mono px-2 py-1 rounded-md border border-border/60 hover:bg-muted transition-colors"
                      data-testid={`wa-insert-${k}-${p.key.replace(/[{}]/g, "")}`}
                    >
                      + {p.key}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {preview && (
        <Dialog open onOpenChange={(v) => !v && setPreview(null)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Preview Pesan</DialogTitle>
              <DialogDescription>Data contoh diisi otomatis untuk pratinjau.</DialogDescription>
            </DialogHeader>
            <div className="rounded-md bg-[#e6fadb] border border-[#c8ecb2] p-4 text-sm whitespace-pre-wrap font-sans" data-testid="wa-preview-content">
              {preview.rendered}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)}>Tutup</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
